import {
  DEMO_GUIDANCE,
  HOST_SEAT_ACTION_LABEL,
  DEMO_SOLO_TASK_REWARDS,
  RESOURCE_LABELS,
  isPlayerKind,
  type DemoNextStep,
  type GameSessionView,
} from '@creativitas/shared';

/**
 * Кто смотрит на демопартию.
 *
 * `solo` — владелец одиночной партии: он и игрок, и тот, кому система
 * подсказывает служебные шаги. `facilitator` — ведущий групповой партии
 * (может и сам сидеть за столом). `guest` — участник по приглашению.
 */
export type DemoViewer =
  | { role: 'solo'; participantId: string | null }
  | { role: 'facilitator'; participantId: string | null }
  | { role: 'guest'; participantId: string };

export type DemoAction =
  | { kind: 'start' }
  | { kind: 'seat' }
  | { kind: 'open_story'; sectorId: string }
  | { kind: 'close_story' }
  | { kind: 'start_talent'; participantId: string }
  | { kind: 'draw'; participantId: string }
  | { kind: 'reveal'; cardId: string }
  | { kind: 'confirm'; cardId: string }
  | { kind: 'resolve'; cardId: string; sectorId: string | null }
  | { kind: 'roll'; participantId: string };

/** Какая подсказка обучения относится к этому шагу. */
export type DemoCoachId =
  | 'board'
  | 'story'
  | 'start_talent'
  | 'card'
  | 'task'
  | 'dice'
  | 'resources'
  | 'invite'
  | 'host_task';

export type DemoStepView = {
  /** Что происходит сейчас. */
  headline: string;
  /** Пояснение рядом с кнопкой — оно остаётся, даже если обучение закрыто. */
  detail: string;
  /** Одно следующее действие этого зрителя, если оно у него есть. */
  action: DemoAction | null;
  actionLabel: string | null;
  coach: DemoCoachId | null;
};

const sectorTitle = (session: GameSessionView, sectorId: string | null) =>
  session.game.board?.sectors.find((sector) => sector.id === sectorId)?.title ?? 'сектор';

const nameOf = (session: GameSessionView, participantId: string | null) =>
  session.participants.find((item) => item.id === participantId)?.displayName ?? 'Участник';

/**
 * Переводит серверный шаг партии на язык конкретного зрителя (ТЗ демо, п. 7.3):
 * что происходит, кто действует и одно следующее действие. Сам шаг считает
 * сервер, поэтому кнопка здесь никогда не предлагает того, что он отклонит.
 */
export function describeDemoStep(session: GameSessionView, viewer: DemoViewer): DemoStepView {
  const step: DemoNextStep | undefined = session.demo?.nextStep;
  const none = (headline: string, detail: string, coach: DemoCoachId | null = null): DemoStepView => ({
    headline,
    detail,
    action: null,
    actionLabel: null,
    coach,
  });
  if (!step) return none('', '');

  const mine = step.participantId !== null && step.participantId === viewer.participantId;
  const who = nameOf(session, step.participantId);
  const solo = viewer.role === 'solo';
  const host = viewer.role === 'facilitator';
  const ownProgress = session.demo?.progress.find((item) => item.participantId === viewer.participantId);

  /* Свой маршрут пройден, а группа ещё играет. */
  if (ownProgress?.finished && session.status === 'active' && !host) {
    return none('Ваш маршрут пройден', 'Три сектора позади. Остальные участники ещё играют — итог откроется, когда партия завершится.');
  }

  switch (step.kind) {
    case 'gather_players': {
      if (host) {
        const seated = session.hostSeatParticipantId !== null;
        return {
          headline: 'Комната ждёт участников',
          detail: 'Отправьте участникам ссылку из кнопки «Пригласить» или участвуйте как игрок сами: вы останетесь ведущим и пройдёте партию вместе с остальными.',
          action: seated ? null : { kind: 'seat' },
          actionLabel: seated ? null : HOST_SEAT_ACTION_LABEL,
          coach: 'invite',
        };
      }
      return none('Ждём участников', 'Партия продолжится, когда за столом будет хотя бы один игрок.');
    }
    case 'start_party':
      return host
        ? {
            headline: 'Все собрались?',
            detail: 'Начните партию: первой прозвучит сказка Маяка, затем каждый получит талант от рождения. Опоздавшие смогут войти и потом.',
            action: { kind: 'start' },
            actionLabel: 'Начать партию',
            coach: 'invite',
          }
        : none('Ждём ведущего', 'Партия начнётся, когда ведущий её запустит.');
    case 'open_story': {
      const title = sectorTitle(session, step.sectorId);
      return host || solo
        ? {
            headline: `Сектор «${title}» ещё не представлен`,
            detail: 'Сначала звучит сказка сектора — её видят все участники. Ход продолжится после неё.',
            action: { kind: 'open_story', sectorId: step.sectorId! },
            actionLabel: `Открыть сказку «${title}»`,
            coach: 'story',
          }
        : none(`Сектор «${title}»`, 'Ведущий откроет сказку сектора — её увидят все.');
    }
    case 'read_story': {
      const title = sectorTitle(session, step.sectorId);
      if (solo) {
        return {
          headline: `Сказка сектора «${title}»`,
          detail: 'Прочитайте сказку — она открывает сектор. Затем продолжите.',
          action: { kind: 'close_story' },
          actionLabel: 'Продолжить',
          coach: 'story',
        };
      }
      return host
        ? {
            headline: `Сказка сектора «${title}» на экране у всех`,
            detail: 'Прочитайте её вслух и закройте, когда группа будет готова продолжить.',
            action: { kind: 'close_story' },
            actionLabel: 'Сказка прочитана',
            coach: 'story',
          }
        : none(`Сказка сектора «${title}»`, 'Ведущий читает сказку. Ход продолжится, когда он её закроет.');
    }
    case 'draw_start_talent': {
      if (!step.participantId) return none('Талант от рождения', 'Каждый участник получает одну карту таланта.');
      if (solo || mine) {
        return {
          headline: 'Ваш талант от рождения',
          detail: 'Первая карта игры — талант, с которым вы пришли в этот мир. С неё начинается путь.',
          action: { kind: 'start_talent', participantId: step.participantId },
          actionLabel: 'Вытянуть талант',
          coach: 'start_talent',
        };
      }
      return host
        ? {
            headline: `${who} получает талант от рождения`,
            detail: 'Участник тянет карту сам. Если он не на связи, вытяните её за него.',
            action: { kind: 'start_talent', participantId: step.participantId },
            actionLabel: `Вытянуть за: ${who}`,
            coach: 'start_talent',
          }
        : none(`${who} получает талант от рождения`, 'Ваш ход наступит по очереди.');
    }
    case 'draw_card': {
      const left = step.remainingDraws > 1 ? ` Осталось карт: ${step.remainingDraws}.` : '';
      if (solo || mine) {
        return {
          headline: 'Вытяните карту клетки',
          detail: `Клетка, на которую встала фишка, даёт карту.${left}`,
          action: { kind: 'draw', participantId: step.participantId! },
          actionLabel: 'Вытянуть карту',
          coach: 'card',
        };
      }
      return host
        ? {
            headline: `${who} тянет карту клетки`,
            detail: `Участник тянет карту сам; при необходимости сделайте это за него.${left}`,
            action: { kind: 'draw', participantId: step.participantId! },
            actionLabel: `Вытянуть за: ${who}`,
            coach: 'card',
          }
        : none(`${who} тянет карту клетки`, 'Следующий бросок — после того, как карта будет отработана.');
    }
    case 'reveal_card': {
      if (solo || mine) {
        return {
          headline: 'Откройте карту',
          detail: 'Переверните карту и прочитайте её. Вопросы карты — для вас: отвечайте вслух или запишите для себя.',
          action: { kind: 'reveal', cardId: step.sessionCardId! },
          actionLabel: 'Открыть карту',
          coach: 'card',
        };
      }
      return host
        ? {
            headline: `${who} открывает карту`,
            detail: 'Участник переворачивает свою карту. Если он не на связи, откройте её за него.',
            action: { kind: 'reveal', cardId: step.sessionCardId! },
            actionLabel: 'Открыть карту',
            coach: 'card',
          }
        : none(`${who} открывает карту`, 'Карту увидят все участники.');
    }
    case 'complete_task': {
      const reward = step.sectorId ? DEMO_SOLO_TASK_REWARDS[step.sectorId] : undefined;
      if (solo) {
        return {
          headline: 'Задание карты',
          detail: 'Ответьте на вопросы карты — вслух или для себя, платформе ответ не нужен. Когда закончите, отметьте выполнение.',
          action: { kind: 'confirm', cardId: step.sessionCardId! },
          actionLabel: reward ? `Задание выполнено · +1 ${RESOURCE_LABELS[reward]}` : 'Задание выполнено',
          coach: 'task',
        };
      }
      if (host) {
        return {
          headline: `${who} отвечает на вопросы карты`,
          detail: reward
            ? `Выслушайте ответ и примите его. По правилам сектора за ответ — «${RESOURCE_LABELS[reward]}».`
            : 'Выслушайте ответ и примите его.',
          action: { kind: 'resolve', cardId: step.sessionCardId!, sectorId: step.sectorId },
          actionLabel: 'Ответ принят',
          coach: 'host_task',
        };
      }
      return mine
        ? none('Ответьте на вопросы карты', 'Расскажите ведущему, что увидели. Ресурс начислит ведущий, когда примет ответ.', 'task')
        : none(`${who} отвечает на вопросы карты`, 'Ведущий примет ответ — и ход продолжится.');
    }
    case 'roll': {
      if (solo || mine) {
        return {
          headline: 'Ваш ход',
          detail: 'Бросьте кубик. Фишка не перескочит следующий сектор: если очков больше, она остановится на его последней клетке.',
          action: { kind: 'roll', participantId: step.participantId! },
          actionLabel: 'Бросить кубик',
          coach: 'dice',
        };
      }
      return host
        ? {
            headline: `Ход: ${who}`,
            detail: 'Участник бросает кубик сам. Если он не на связи, бросьте за него.',
            action: { kind: 'roll', participantId: step.participantId! },
            actionLabel: `Бросить за: ${who}`,
            coach: 'dice',
          }
        : none(`Ход: ${who}`, 'Ждём броска.');
    }
    case 'paused':
      return none('Партия на паузе', 'Состояние сохранено. Игровые действия вернутся, когда партию продолжат.');
    case 'completed':
      return none('Демо завершено', 'Итог — в окне «Итог путешествия». Сектора полной игры подсвечены на поле.');
  }
}

/** Игроки демопартии в порядке хода — для полоски маршрута. */
export const demoPlayers = (session: GameSessionView) =>
  session.participants.filter((participant) => isPlayerKind(participant.kind));

/**
 * Методическое пояснение к текущему сектору (ТЗ демо, п. 7.2).
 *
 * Два шага: пока карты сектора у игрока нет — что сейчас произойдёт и о чём
 * сектор; когда карта получена и открыта — вопросы к ней. Вопросы «посмотрите
 * на карту таланта» до появления карты сбивали с толку. Гостю группы
 * пояснения ведущего не показываются вовсе.
 */
export function demoGuidance(
  session: GameSessionView,
  viewer: DemoViewer,
): { intro: string | null; text: string; stage: 'before' | 'card' } | null {
  const demo = session.demo;
  if (!demo || viewer.role === 'guest' || session.status === 'completed') return null;
  const texts = DEMO_GUIDANCE[viewer.role === 'solo' ? 'solo' : 'facilitator'];
  const step = demo.nextStep;
  const subjectId = step.participantId ?? viewer.participantId;
  const subject = session.participants.find((item) => item.id === subjectId);
  const position = subject?.boardPosition;
  const cellSector = position !== null && position !== undefined
    ? session.game.board?.cells[position]?.sectorId
    : undefined;
  const lighthouse = demo.routeSectorIds[0]!;
  const opening = session.status === 'waiting' || session.phase !== 'playing' ||
    step.kind === 'draw_start_talent';
  const sectorId = opening ? lighthouse : step.sectorId ?? cellSector ?? null;
  const sector = sectorId ? texts.sectors[sectorId] : undefined;
  if (!sector) return opening ? { intro: null, text: texts.intro, stage: 'before' } : null;

  const own = session.drawnCards.filter((card) => card.drawnByParticipantId === subjectId);
  /* Последняя карта сектора у этого игрока — карты идут в порядке вытягивания. */
  const sectorCard = own.filter((card) => card.reason === 'sector_event' && card.sectorId === sectorId).at(-1);
  const cardReady = sectorId === lighthouse
    /* Маяк — это талант от рождения: вопросы к нему, когда он открыт. */
    ? own.some((card) => card.reason === 'start_talent' && card.revealed) &&
      step.kind !== 'draw_start_talent'
    /*
      Дракон, Замок: вопросы — как только открыта карта сектора. На клетке «Т»
      после неё открывается ещё талант, и подсказка ждала его, а ведущему она
      нужна раньше — пока игрок обсуждает карту роли или Дракона (19.10.2026).
      Пока для клетки тянутся карты, пояснение остаётся вводным: открытая
      карта прошлого захода в сектор к новой клетке не относится.
    */
    : step.kind === 'complete_task' ||
      ((step.kind === 'reveal_card' || step.kind === 'roll') && Boolean(sectorCard?.revealed));

  return {
    intro: session.status === 'waiting' || session.phase === 'intro' ? texts.intro : null,
    text: cardReady ? sector.card : sector.before,
    stage: cardReady ? 'card' : 'before',
  };
}

/** Пояснение к сказке сектора: сказка всегда звучит до карты. */
export function demoStoryGuidance(viewer: DemoViewer | undefined, sectorId: string | null): string | null {
  if (!viewer || viewer.role === 'guest' || !sectorId) return null;
  return DEMO_GUIDANCE[viewer.role === 'solo' ? 'solo' : 'facilitator'].sectors[sectorId]?.before ?? null;
}
